import { useCallback, useEffect, useState } from "react";
import {
  Activity,
  ArrowLeft,
  Building2,
  CheckCircle2,
  FolderKanban,
  LogOut,
  Plus,
  ReceiptText,
  Search,
  Settings,
  ShieldCheck,
  Users,
} from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { canAccessGlobalAdmin } from "../auth/access";
import { Logo } from "../components/Logo";
import { FormActions, Modal } from "../components/Modal";
import { StatusPill } from "../components/Surface";
import { runtimeUrls, tenantUrl } from "../config/domains";
import {
  MAX_PROJECT_FILE_BYTES,
  PROJECT_FILE_LIMIT_MB,
} from "../lib/files";
import { getSupabaseAccessToken, supabase } from "../lib/supabase";
import { formatDate } from "../lib/format";
import type { Organization, Project, QuoteStatus, Role } from "../types";

type Section =
  | "clientes"
  | "proyectos"
  | "usuarios"
  | "cotizaciones"
  | "actividad"
  | "configuracion";
interface AdminMember {
  id: string;
  organization_id: string;
  user_id: string | null;
  email: string;
  full_name: string;
  role: Role;
  status: "INVITED" | "ACTIVE" | "DISABLED";
  invitation_sent_at: string | null;
  last_access_at: string | null;
  projects: string[];
}
interface AdminQuote {
  id: string;
  organization_id: string;
  project_id: string;
  organization_name: string;
  project_name: string;
  quote_number: string | null;
  status: QuoteStatus;
  payment_status: string;
  subtotal: number;
  total: number;
  paid: number;
  updated_at: string;
}
interface AdminActivity {
  id: string;
  organization_id: string;
  project_id: string | null;
  organization_name: string;
  project_name: string | null;
  actor_name: string;
  action: string;
  entity_type: string;
  description: string;
  created_at: string;
}
interface AdminSnapshot {
  organizations: Organization[];
  projects: Project[];
  members: AdminMember[];
  quotes: AdminQuote[];
  activity: AdminActivity[];
  settings: Record<string, unknown>;
}

const modules = [
  { id: "clientes", label: "Clientes", icon: Building2 },
  { id: "proyectos", label: "Proyectos", icon: FolderKanban },
  { id: "usuarios", label: "Usuarios", icon: Users },
  { id: "cotizaciones", label: "Cotizaciones", icon: ReceiptText },
  { id: "actividad", label: "Actividad", icon: Activity },
  { id: "configuracion", label: "Configuración", icon: Settings },
] as const;

function apiUrl(path: string) {
  return `${runtimeUrls().apiUrl.replace(/\/$/, "")}${path}`;
}

export function AdminConsole() {
  const {
    user,
    loading: authLoading,
    accessContext,
    signOut,
    getMfaStatus,
  } = useAuth();
  const location = useLocation();
  const section = (location.pathname.split("/")[2] || "clientes") as Section;
  const [snapshot, setSnapshot] = useState<AdminSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [organization, setOrganization] = useState<
    Organization | null | undefined
  >(undefined);
  const [project, setProject] = useState<Project | null | undefined>(undefined);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteOrganization, setInviteOrganization] = useState("");
  const [projectMember, setProjectMember] = useState<AdminMember | null>(null);
  const [mfaConfigured, setMfaConfigured] = useState<boolean | null>(null);
  const refresh = useCallback(async () => {
    if (!supabase) return;
    setLoading(true);
    setError(null);
    const { data, error: rpcError } = await supabase.rpc("get_admin_snapshot");
    if (rpcError) setError(rpcError.message);
    else if (!data) setError("La sesión Owner requiere MFA AAL2.");
    else setSnapshot(data as AdminSnapshot);
    setLoading(false);
  }, []);
  useEffect(() => {
    if (canAccessGlobalAdmin(accessContext)) void refresh();
  }, [accessContext, refresh]);
  useEffect(() => {
    if (!canAccessGlobalAdmin(accessContext)) return;
    let active = true;
    void getMfaStatus()
      .then((status) => {
        if (active)
          setMfaConfigured(
            status.factors.some(
              (factor) => factor.status.toLowerCase() === "verified",
            ),
          );
      })
      .catch(() => {
        if (active) setMfaConfigured(null);
      });
    return () => {
      active = false;
    };
  }, [accessContext, getMfaStatus]);
  const notify = (message: string) => {
    setNotice(message);
    window.setTimeout(() => setNotice(null), 3500);
  };
  const callApi = async (
    path: string,
    body: unknown = {},
    method: "POST" | "DELETE" = "POST",
  ) => {
    const token = getSupabaseAccessToken();
    if (!token) throw new Error("La sesión expiró.");
    let response: Response;
    try {
      response = await fetch(apiUrl(path), {
        method,
        credentials: "include",
        cache: "no-store",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: method === "DELETE" ? undefined : JSON.stringify(body),
      });
    } catch {
      throw new Error(
        "No pudimos conectar con el servicio de administración. Inténtalo de nuevo en unos minutos.",
      );
    }
    const result = (await response.json().catch(() => ({}))) as {
      detail?: string;
    };
    if (!response.ok)
      throw new Error(result.detail ?? "La operación no pudo completarse.");
    return result;
  };
  if (authLoading)
    return (
      <div className="app-loader">
        <span />
        <strong>POSITRONICO</strong>
      </div>
    );
  if (!user)
    return (
      <main className="access-error">
        <Logo />
        <span>401</span>
        <h1>Sesión requerida</h1>
        <p>Inicia sesión para administrar Positrónico.</p>
      </main>
    );
  if (!canAccessGlobalAdmin(accessContext))
    return (
      <main className="access-error">
        <Logo />
        <span>403</span>
        <h1>MFA requerido</h1>
        <p>El Back office requiere una sesión Owner verificada en AAL2.</p>
      </main>
    );
  const organizations = snapshot?.organizations ?? [];
  const projects = snapshot?.projects ?? [];
  const members = snapshot?.members ?? [];
  const quotes = snapshot?.quotes ?? [];
  const activities = snapshot?.activity ?? [];
  const matches = (value: string) =>
    value.toLowerCase().includes(query.toLowerCase());
  const saveOrganization = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!supabase) return;
    const values = new FormData(event.currentTarget);
    const record = {
      name: String(values.get("name")),
      slug: String(
        values.get("slug") ?? organization?.slug ?? "",
      ).toLowerCase(),
      is_active: values.get("is_active") === "on",
    };
    const result = organization?.id
      ? await supabase
          .from("organizations")
          .update(record)
          .eq("id", organization.id)
      : await supabase.from("organizations").insert(record);
    if (result.error) setError(result.error.message);
    else {
      setOrganization(undefined);
      notify("Cliente guardado.");
      await refresh();
    }
  };
  const saveProject = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!supabase) return;
    const values = new FormData(event.currentTarget);
    const record = {
      organization_id: String(values.get("organization_id")),
      name: String(values.get("name")),
      slug: String(values.get("slug")).toLowerCase(),
      description: String(values.get("description")),
      status: String(values.get("status")),
      archived_at:
        values.get("archived") === "on" ? new Date().toISOString() : null,
    };
    const result = project?.id
      ? await supabase.from("projects").update(record).eq("id", project.id)
      : await supabase.from("projects").insert(record);
    if (result.error) setError(result.error.message);
    else {
      setProject(undefined);
      notify("Proyecto guardado.");
      await refresh();
    }
  };
  const invite = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    if (values.getAll("project_ids").length === 0) {
      setError("Selecciona al menos un proyecto para el usuario.");
      return;
    }
    try {
      await callApi("/api/invitations", {
        email: String(values.get("email")),
        organization_id: String(values.get("organization_id")),
        project_ids: values.getAll("project_ids"),
        role: String(values.get("role")),
      });
      setInviteOpen(false);
      setInviteOrganization("");
      notify("Invitación enviada.");
      await refresh();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "No se pudo invitar.",
      );
    }
  };
  const deleteMember = async (member: AdminMember) => {
    if (!window.confirm(`¿Eliminar definitivamente a ${member.full_name}? Esta acción sólo continuará si no existe historial importante.`)) return;
    try {
      await callApi(`/api/admin/members/${member.id}`, undefined, "DELETE");
      notify("Usuario eliminado definitivamente.");
      await refresh();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "No se pudo eliminar el usuario.");
    }
  };
  const updateMember = async (member: AdminMember, body: unknown) => {
    try {
      await callApi(`/api/admin/members/${member.id}`, body);
      notify("Usuario actualizado.");
      await refresh();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "No se pudo actualizar.",
      );
    }
  };
  const saveMemberProjects = async (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();
    if (!projectMember) return;
    const values = new FormData(event.currentTarget);
    await updateMember(projectMember, {
      project_ids: values.getAll("project_ids"),
    });
    setProjectMember(null);
  };
  const saveSettings = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!supabase) return;
    const values = new FormData(event.currentTarget);
    const record = {
      id: true,
      brand_name: String(values.get("brand_name")),
      general_email: String(values.get("general_email")),
      timezone: String(values.get("timezone")),
      default_currency: String(values.get("default_currency")).toUpperCase(),
      default_tax_rate: Number(values.get("default_tax_rate")),
      default_quote_validity_days: Number(
        values.get("default_quote_validity_days"),
      ),
      quote_prefix: String(values.get("quote_prefix")),
      default_payment_terms: String(values.get("default_payment_terms")),
      email_notifications_enabled:
        values.get("email_notifications_enabled") === "on",
      default_project_status: String(values.get("default_project_status")),
      max_file_size_bytes: Number(values.get("max_file_size_mb")) * 1024 * 1024,
    };
    const { error: settingsError } = await supabase
      .from("platform_settings")
      .upsert(record);
    if (settingsError) setError(settingsError.message);
    else {
      notify("Configuración guardada.");
      await refresh();
    }
  };
  return (
    <main className="admin-console">
      <header>
        <Logo />
        <nav>
          <a href={runtimeUrls().appUrl}>
            <ArrowLeft size={16} />
            Workspaces
          </a>
          <button className="button secondary" onClick={() => void signOut()}>
            <LogOut size={16} />
            Cerrar sesión
          </button>
        </nav>
      </header>
      <aside>
        {modules.map(({ id, label, icon: Icon }) => (
          <NavLink
            key={id}
            to={`/admin/${id}`}
            className={section === id ? "active" : ""}
          >
            <Icon size={18} />
            <span>{label}</span>
          </NavLink>
        ))}
      </aside>
      <section className="admin-content">
        <div className="admin-title">
          <div>
            <span className="eyebrow">BACK OFFICE</span>
            <h1>
              {modules.find((item) => item.id === section)?.label ??
                "Administración"}
            </h1>
          </div>
          <label className="admin-search">
            <Search size={17} />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Buscar…"
            />
          </label>
        </div>
        {loading && (
          <div className="page-loading">
            <span />
            <p>Cargando datos…</p>
          </div>
        )}
        {error && (
          <div className="admin-alert error">
            <b>Error</b>
            <span>{error}</span>
            <button onClick={() => setError(null)}>Cerrar</button>
          </div>
        )}
        {!loading && snapshot && section === "clientes" && (
          <>
            <div className="admin-actions">
              <p>Organizaciones, estado, proyectos y usuarios.</p>
              <button
                className="button primary"
                onClick={() => setOrganization(null)}
              >
                <Plus size={17} />
                Nuevo cliente
              </button>
            </div>
            <div className="admin-table">
              <div className="admin-table-head cols-client">
                <span>Cliente</span>
                <span>Estado</span>
                <span>Proyectos</span>
                <span>Usuarios</span>
                <span>Acciones</span>
              </div>
              {organizations
                .filter((item) => matches(`${item.name} ${item.slug}`))
                .map((item) => (
                  <article className="cols-client" key={item.id}>
                    <div>
                      <b>{item.name}</b>
                      <small>{item.slug}.positronico.com</small>
                    </div>
                    <StatusPill tone={item.is_active ? "green" : "gray"}>
                      {item.is_active ? "Activa" : "Inactiva"}
                    </StatusPill>
                    <span>
                      {
                        projects.filter(
                          (projectItem) =>
                            projectItem.organization_id === item.id &&
                            !projectItem.archived_at,
                        ).length
                      }
                    </span>
                    <span>
                      {
                        members.filter(
                          (member) => member.organization_id === item.id,
                        ).length
                      }
                    </span>
                    <div className="row-actions">
                      <button onClick={() => setOrganization(item)}>
                        Editar
                      </button>
                      <a href={tenantUrl(item.slug)}>Entrar</a>
                    </div>
                  </article>
                ))}
            </div>
          </>
        )}
        {!loading && snapshot && section === "proyectos" && (
          <>
            <div className="admin-actions">
              <p>Portafolio global por cliente y estado.</p>
              <button
                className="button primary"
                onClick={() => setProject(null)}
              >
                <Plus size={17} />
                Nuevo proyecto
              </button>
            </div>
            <div className="admin-table">
              <div className="admin-table-head cols-project">
                <span>Proyecto</span>
                <span>Cliente</span>
                <span>Estado</span>
                <span>Actualizado</span>
                <span>Acciones</span>
              </div>
              {projects
                .filter((item) => matches(`${item.name} ${item.description}`))
                .map((item) => (
                  <article className="cols-project" key={item.id}>
                    <div>
                      <b>{item.name}</b>
                      <small>{item.description}</small>
                    </div>
                    <span>
                      {
                        organizations.find(
                          (org) => org.id === item.organization_id,
                        )?.name
                      }
                    </span>
                    <StatusPill tone={item.archived_at ? "gray" : "green"}>
                      {item.archived_at ? "Archivado" : item.status}
                    </StatusPill>
                    <time>{formatDate(item.updated_at)}</time>
                    <div className="row-actions">
                      <button onClick={() => setProject(item)}>Editar</button>
                      <a
                        href={tenantUrl(
                          organizations.find(
                            (org) => org.id === item.organization_id,
                          )?.slug ?? "",
                        )}
                      >
                        Entrar
                      </a>
                    </div>
                  </article>
                ))}
            </div>
          </>
        )}
        {!loading && snapshot && section === "usuarios" && (
          <>
            <div className="admin-actions">
              <p>Invitation-only, roles, estado y asignación de proyectos.</p>
              <button
                className="button primary"
                onClick={() => setInviteOpen(true)}
              >
                <Plus size={17} />
                Invitar usuario
              </button>
            </div>
            <div className="admin-table">
              <div className="admin-table-head cols-user">
                <span>Usuario</span>
                <span>Cliente / proyectos</span>
                <span>Rol</span>
                <span>Estado</span>
                <span>Acciones</span>
              </div>
              {members
                .filter((item) => matches(`${item.full_name} ${item.email}`))
                .map((member) => (
                  <article className="cols-user" key={member.id}>
                    <div>
                      <b>{member.full_name}</b>
                      <small>
                        {member.email}
                        <br />
                        Último acceso: {formatDate(member.last_access_at)}
                      </small>
                    </div>
                    <div>
                      <span>
                        {
                          organizations.find(
                            (org) => org.id === member.organization_id,
                          )?.name
                        }
                      </span>
                      <small>
                        {member.projects
                          ?.map(
                            (id) =>
                              projects.find(
                                (projectItem) => projectItem.id === id,
                              )?.name,
                          )
                          .filter(Boolean)
                          .join(", ") || "Sin proyecto"}
                      </small>
                    </div>
                    <select
                      value={member.role}
                      disabled={member.role === "POSITRONICO_OWNER"}
                      onChange={(event) =>
                        void updateMember(member, { role: event.target.value })
                      }
                    >
                      <option>CLIENT_ADMIN</option>
                      <option>CLIENT_MEMBER</option>
                      {member.role === "POSITRONICO_OWNER" && (
                        <option>POSITRONICO_OWNER</option>
                      )}
                    </select>
                    <StatusPill
                      tone={
                        member.status === "ACTIVE"
                          ? "green"
                          : member.status === "INVITED"
                            ? "blue"
                            : "gray"
                      }
                    >
                      {member.status}
                    </StatusPill>
                    <div className="row-actions">
                      {member.role !== "POSITRONICO_OWNER" && (
                        <button onClick={() => setProjectMember(member)}>
                          Proyectos
                        </button>
                      )}
                      {member.status === "INVITED" && (
                        <>
                          <button
                            onClick={() =>
                              void callApi(
                                `/api/admin/members/${member.id}/resend`,
                              ).then(refresh)
                            }
                          >
                            Reenviar
                          </button>
                          <button
                            onClick={() =>
                              void callApi(
                                `/api/admin/members/${member.id}/cancel`,
                              ).then(refresh)
                            }
                          >
                            Cancelar
                          </button>
                        </>
                      )}
                      {member.role !== "POSITRONICO_OWNER" && (
                        <>
                          <button
                            onClick={() =>
                              void updateMember(member, {
                                status:
                                  member.status === "DISABLED"
                                    ? "ACTIVE"
                                    : "DISABLED",
                              })
                            }
                          >
                            {member.status === "DISABLED"
                              ? "Reactivar"
                              : "Desactivar"}
                          </button>
                          <button onClick={() => void deleteMember(member)}>
                            Eliminar definitivamente
                          </button>
                        </>
                      )}
                    </div>
                  </article>
                ))}
            </div>
          </>
        )}
        {!loading && snapshot && section === "cotizaciones" && (
          <>
            <div className="admin-actions">
              <p>Vista comercial global con cobrado y saldo.</p>
            </div>
            <div className="admin-table">
              <div className="admin-table-head cols-quote">
                <span>Cotización</span>
                <span>Cliente / proyecto</span>
                <span>Estado</span>
                <span>Total</span>
                <span>Saldo</span>
              </div>
              {quotes
                .filter((item) =>
                  matches(
                    `${item.quote_number} ${item.organization_name} ${item.project_name}`,
                  ),
                )
                .map((quote) => (
                  <article className="cols-quote" key={quote.id}>
                    <div>
                      <b>{quote.quote_number ?? "Borrador"}</b>
                      <small>Actualizada {formatDate(quote.updated_at)}</small>
                    </div>
                    <div>
                      <span>{quote.organization_name}</span>
                      <small>{quote.project_name}</small>
                    </div>
                    <StatusPill
                      tone={quote.status === "APPROVED" ? "green" : "blue"}
                    >
                      {quote.status}
                    </StatusPill>
                    <b>
                      $
                      {Number(quote.total).toLocaleString("es-MX", {
                        minimumFractionDigits: 2,
                      })}
                    </b>
                    <div>
                      <span>
                        $
                        {Math.max(
                          0,
                          Number(quote.total) - Number(quote.paid),
                        ).toLocaleString("es-MX", { minimumFractionDigits: 2 })}
                      </span>
                      <a
                        className="text-button"
                        href={`${tenantUrl(organizations.find((item) => item.id === quote.organization_id)?.slug ?? "")}/cotizaciones?quote=${quote.id}`}
                      >
                        Ver detalle
                      </a>
                    </div>
                  </article>
                ))}
            </div>
          </>
        )}
        {!loading && snapshot && section === "actividad" && (
          <>
            <div className="admin-actions">
              <p>Auditoría global por usuario, cliente, proyecto y entidad.</p>
            </div>
            <div className="activity-admin">
              {activities
                .filter((item) =>
                  matches(
                    `${item.actor_name} ${item.action} ${item.organization_name} ${item.project_name} ${item.entity_type}`,
                  ),
                )
                .map((item) => (
                  <article key={item.id}>
                    <time>
                      {new Date(item.created_at).toLocaleString("es-MX")}
                    </time>
                    <b>{item.actor_name}</b>
                    <span>{item.description}</span>
                    <small>
                      {item.organization_name}
                      {item.project_name
                        ? ` · ${item.project_name}`
                        : ""} · {item.entity_type}
                    </small>
                  </article>
                ))}
            </div>
          </>
        )}
        {!loading && snapshot && section === "configuracion" && (
          <SettingsForm
            settings={snapshot.settings}
            mfaConfigured={mfaConfigured}
            onSubmit={saveSettings}
          />
        )}
      </section>
      {notice && (
        <div className="toast" role="status">
          <CheckCircle2 size={17} />
          {notice}
        </div>
      )}
      <Modal
        open={organization !== undefined}
        title={organization ? "Editar cliente" : "Nuevo cliente"}
        onClose={() => setOrganization(undefined)}
      >
        <form className="form-grid" onSubmit={saveOrganization}>
          <label className="full">
            Nombre
            <input
              name="name"
              defaultValue={organization?.name ?? ""}
              required
            />
          </label>
          <label>
            Slug
            <input
              name="slug"
              pattern="[a-z0-9-]+"
              defaultValue={organization?.slug ?? ""}
              required
              disabled={Boolean(organization?.id)}
            />
          </label>
          <label className="check-field">
            <input
              type="checkbox"
              name="is_active"
              defaultChecked={organization?.is_active ?? true}
            />
            Cliente activo
          </label>
          <FormActions onCancel={() => setOrganization(undefined)} />
        </form>
      </Modal>
      <Modal
        open={project !== undefined}
        title={project ? "Editar proyecto" : "Nuevo proyecto"}
        onClose={() => setProject(undefined)}
      >
        <form className="form-grid" onSubmit={saveProject}>
          <label>
            Cliente
            <select
              name="organization_id"
              defaultValue={project?.organization_id ?? ""}
              required
            >
              <option value="">Seleccionar…</option>
              {organizations.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Nombre
            <input name="name" defaultValue={project?.name ?? ""} required />
          </label>
          <label>
            Slug
            <input
              name="slug"
              pattern="[a-z0-9-]+"
              defaultValue={project?.slug ?? ""}
              required
            />
          </label>
          <label>
            Estado
            <input
              name="status"
              defaultValue={project?.status ?? "Activo"}
              required
            />
          </label>
          <label className="full">
            Descripción
            <textarea
              name="description"
              defaultValue={project?.description ?? ""}
            />
          </label>
          <label className="check-field">
            <input
              type="checkbox"
              name="archived"
              defaultChecked={Boolean(project?.archived_at)}
            />
            Archivado
          </label>
          <FormActions onCancel={() => setProject(undefined)} />
        </form>
      </Modal>
      <Modal
        open={inviteOpen}
        title="Invitar usuario"
        onClose={() => {
          setInviteOpen(false);
          setInviteOrganization("");
        }}
      >
        <form className="form-grid" onSubmit={invite}>
          <label className="full">
            Email
            <input type="email" name="email" required />
          </label>
          <label>
            Cliente
            <select
              name="organization_id"
              value={inviteOrganization}
              onChange={(event) => setInviteOrganization(event.target.value)}
              required
            >
              <option value="">Seleccionar…</option>
              {organizations
                .filter((item) => item.is_active)
                .map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
            </select>
          </label>
          <label>
            Rol
            <select name="role" defaultValue="CLIENT_MEMBER">
              <option>CLIENT_ADMIN</option>
              <option>CLIENT_MEMBER</option>
            </select>
          </label>
          <label className="full">
            Proyectos
            <select
              name="project_ids"
              multiple
              required
              size={Math.min(6, Math.max(2, projects.length))}
            >
              {projects
                .filter(
                  (item) =>
                    item.organization_id === inviteOrganization &&
                    !item.archived_at,
                )
                .map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
            </select>
          </label>
          <FormActions
            onCancel={() => {
              setInviteOpen(false);
              setInviteOrganization("");
            }}
          />
        </form>
      </Modal>
      <Modal
        open={Boolean(projectMember)}
        title="Asignar proyectos"
        onClose={() => setProjectMember(null)}
      >
        {projectMember && (
          <form className="form-grid" onSubmit={saveMemberProjects}>
            <p className="full muted-copy">
              Selecciona los proyectos de {projectMember.full_name}.
            </p>
            <label className="full">
              Proyectos
              <select
                name="project_ids"
                multiple
                defaultValue={projectMember.projects ?? []}
                size={Math.min(
                  8,
                  Math.max(
                    2,
                    projects.filter(
                      (item) =>
                        item.organization_id === projectMember.organization_id &&
                        !item.archived_at,
                    ).length,
                  ),
                )}
              >
                {projects
                  .filter(
                    (item) =>
                      item.organization_id === projectMember.organization_id &&
                      !item.archived_at,
                  )
                  .map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
              </select>
            </label>
            <FormActions onCancel={() => setProjectMember(null)} />
          </form>
        )}
      </Modal>
    </main>
  );
}

function SettingsForm({
  settings,
  mfaConfigured,
  onSubmit,
}: {
  settings: Record<string, unknown>;
  mfaConfigured: boolean | null;
  onSubmit(event: React.FormEvent<HTMLFormElement>): void;
}) {
  return (
    <form className="settings-v1" onSubmit={onSubmit}>
      <section>
        <h2>General</h2>
        <label>
          Nombre
          <input
            name="brand_name"
            defaultValue={String(settings.brand_name ?? "Positrónico")}
          />
        </label>
        <label>
          Email general
          <input
            type="email"
            name="general_email"
            defaultValue={String(
              settings.general_email ?? "contacto.positronico@gmail.com",
            )}
          />
        </label>
        <label>
          Timezone
          <input
            name="timezone"
            defaultValue={String(settings.timezone ?? "America/Mexico_City")}
          />
        </label>
      </section>
      <section>
        <h2>Usuarios y acceso</h2>
        <div className="settings-readonly">
          <ShieldCheck />
          <span>
            Invitation-only <b>Activo</b>
          </span>
          <span>
            Contraseña <b>12+ / A-Z / a-z / 0-9</b>
          </span>
          <span>
            MFA Owner <b>Obligatorio</b>
          </span>
          <span>
            Autenticador TOTP{" "}
            <b>
              {mfaConfigured === true
                ? "Configurado"
                : mfaConfigured === false
                  ? "Pendiente"
                  : "No disponible"}
            </b>
          </span>
          <details className="mfa-recovery-note">
            <summary>Recuperación segura de MFA</summary>
            <p>
              No existe un bypass público ni una desactivación simple. Ante
              pérdida del dispositivo, el procedimiento break-glass exige
              verificar identidad, revocar sesiones, retirar únicamente el
              factor perdido desde administración segura y enrolar un QR
              nuevo.
            </p>
          </details>
        </div>
      </section>
      <section>
        <h2>Cotizaciones</h2>
        <label>
          Moneda
          <input
            name="default_currency"
            maxLength={3}
            defaultValue={String(settings.default_currency ?? "MXN")}
          />
        </label>
        <label>
          IVA default
          <input
            type="number"
            name="default_tax_rate"
            min="0"
            max="100"
            defaultValue={Number(settings.default_tax_rate ?? 16)}
          />
        </label>
        <label>
          Vigencia (días)
          <input
            type="number"
            name="default_quote_validity_days"
            min="1"
            defaultValue={Number(settings.default_quote_validity_days ?? 15)}
          />
        </label>
        <label>
          Prefijo
          <input
            name="quote_prefix"
            defaultValue={String(settings.quote_prefix ?? "COT-")}
          />
        </label>
        <label className="wide">
          Condiciones default
          <textarea
            name="default_payment_terms"
            defaultValue={String(settings.default_payment_terms ?? "")}
          />
        </label>
      </section>
      <section>
        <h2>Notificaciones</h2>
        <label className="check-field">
          <input
            type="checkbox"
            name="email_notifications_enabled"
            defaultChecked={settings.email_notifications_enabled !== false}
          />
          Emails habilitados
        </label>
        <div className="settings-readonly">
          Remitente: Positrónico &lt;contacto.positronico@gmail.com&gt;
        </div>
      </section>
      <section>
        <h2>Proyectos y archivos</h2>
        <label>
          Estado default
          <input
            name="default_project_status"
            defaultValue={String(settings.default_project_status ?? "Activo")}
          />
        </label>
        <label>
          Máximo MB
          <input
            type="number"
            name="max_file_size_mb"
            min="1"
            max={PROJECT_FILE_LIMIT_MB}
            defaultValue={
              Number(settings.max_file_size_bytes ?? MAX_PROJECT_FILE_BYTES) /
              1024 /
              1024
            }
          />
        </label>
      </section>
      <section>
        <h2>Integraciones</h2>
        <div className="integration-grid">
          <span>
            Supabase <b>Conectado</b>
          </span>
          <span>
            Render API <b>Operativo</b>
          </span>
          <span>
            Cloudflare <b>Operativo</b>
          </span>
          <span>
            Email <b>Conectado</b>
          </span>
          <span>
            LLM <b>No configurado</b>
          </span>
        </div>
      </section>
      <button className="button primary" type="submit">
        Guardar configuración
      </button>
    </form>
  );
}
